import cn from 'classnames';
import { parameterize } from 'inflected';
import {
  MdOutlineAccountTree,
  MdOutlineBadge,
  MdOutlineDns,
  MdOutlineExtension,
  MdOutlineGpsFixed,
  MdOutlineHub,
  MdOutlineInventory2,
  MdOutlineLabel,
  MdOutlineModelTraining,
  MdOutlinePublic,
  MdOutlineSmartToy,
  MdSubdirectoryArrowRight
} from 'react-icons/md';
import { IconType } from 'react-icons';

// Shared visual language between the table row and the product detail card -- the card
// "recapitulates" the row's metadata per Sean, so both need the exact same chips/capsules
// rather than a second, slightly-different rendering of the same facets.

interface TagListProps {
  values: string[];
  colorMap: Record<string, string>;
}

// Bordered/soft "tag" chips (not solid fills) -- one hue per value, via the shared open-ended
// categorical palette (facetColorMap). Function is the only facet still shown this way; Kind/
// Scope/Audience use the solid IconChip treatment below (fixed, small value sets).
export const TagList = ({ values, colorMap }: TagListProps) => (
  <div className='product-roadmap-tag-list'>
    {values.map(v => (
      <span
        key={v}
        className='product-roadmap-tag'
        style={{ borderColor: colorMap[v], color: colorMap[v] }}
      >
        {v}
      </span>
    ))}
  </div>
);

// Kind/Audience/Scope each have a small, fixed set of known values (2-4 each) -- rather than the
// open-ended categorical palette Function uses, each value gets a hand-picked icon + solid color,
// distinct from the Success/Warning/Danger/DivergentPositive/Brand60/Neutral400 hues already used
// for status elsewhere on this page (row accent bar, status strip), so a chip is never misread as
// a status signal. Icon-only (no label) to stay compact -- `title` carries the value for a hover
// tooltip.
export const KIND_ICON: Record<string, IconType> = {
  Program: MdOutlineAccountTree,
  Product: MdOutlineInventory2,
  Feature: MdOutlineExtension,
  Infra: MdOutlineDns
};

export const AUDIENCE_ICON: Record<string, IconType> = {
  Internal: MdOutlineBadge,
  'Client-facing': MdOutlinePublic
};

// `intelligence` differs from the three below it in one way that matters: it is multi-value and
// often absent (31 of 71 products carry no tag), so callers map over the array and render nothing
// when it is empty -- rather than every product carrying exactly one chip.
export const INTELLIGENCE_ICON: Record<string, IconType> = {
  'Artificial intelligence': MdOutlineSmartToy,
  'Machine learning': MdOutlineModelTraining
};

export const SCOPE_ICON: Record<string, IconType> = {
  'Cross-solution': MdOutlineHub,
  'Solution-specific': MdOutlineGpsFixed
};

interface IconChipProps {
  facet: 'kind' | 'audience' | 'scope' | 'intelligence';
  value: string;
  iconMap: Record<string, IconType>;
}

export const IconChip = ({ facet, value, iconMap }: IconChipProps) => {
  const Icon = iconMap[value] ?? MdOutlineLabel;
  return (
    <span
      className={cn('product-roadmap-icon-chip', `${facet}-${parameterize(value)}`)}
      title={value}
    >
      <Icon />
    </span>
  );
};

interface ParentBreadcrumbProps {
  parent: string;
}

// A lineage marker, not a categorical tag -- deliberately uncolored (unlike TagList's per-value
// palette) so it reads as "what this rolls up into" rather than competing visually with the
// Function/Kind/Scope/Audience classification facets. Renders nothing for the ~half of products
// (standalone products, and Programs themselves) that have no parent.
export const ParentBreadcrumb = ({ parent }: ParentBreadcrumbProps) =>
  parent ? (
    <span className='product-roadmap-parent-breadcrumb'>
      <MdSubdirectoryArrowRight />
      {parent}
    </span>
  ) : null;
